import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { ShieldCheck, Eye } from "lucide-react";

const DQ_COLUMNS = ["Booking", "Date", "Reg No", "Customer", ""];

export default function DataQuality() {
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const fetchChecks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/data-quality`, { withCredentials: true });
      const list = res.data?.checks || [];
      setChecks(list);
      setActiveId((prev) => prev || (list.find((c) => c.count > 0) || list[0] || {}).id);
    } catch { toast.error("Failed to load data quality checks"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchChecks(); }, [fetchChecks]);

  const active = checks.find((c) => c.id === activeId);
  const activeRows = active ? active.rows : [];

  const openRecord = (row) => {
    if (row.link === "appointment") navigate(`/appointments/${row.record_id}`);
    else navigate(`/reception/${row.record_id}`);
  };

  return (
    <div className="space-y-6" data-testid="data-quality-page">
      <PageHeader
        icon={ShieldCheck}
        title="Data Quality"
        subtitle="Records that need correcting"
        onRefresh={fetchChecks}
        refreshTestId="dq-refresh-btn"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {checks.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveId(c.id)}
            className={`text-left rounded-sm p-4 border transition-colors ${activeId === c.id ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}
            data-testid={`dq-card-${c.id}`}
          >
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`text-2xl mt-1 ${c.count === 0 ? "text-green-600" : ""}`}>{c.count}</p>
          </button>
        ))}
      </div>

      {active && (
        <div>
          <h2 className="text-[11px] uppercase tracking-widest font-bold text-gray-500 mb-2">{active.label}</h2>
          <Card className="border border-gray-200 rounded-sm shadow-none overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  {DQ_COLUMNS.map((h, i) => (
                    <TableHead key={i} className="text-[10px] uppercase tracking-widest font-bold text-gray-500 whitespace-nowrap text-center">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-400">Loading...</TableCell></TableRow>
                ) : activeRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-gray-400">Nothing to correct</TableCell></TableRow>
                ) : activeRows.map((r) => (
                  <TableRow key={r.record_id} className="hover:bg-gray-50/50">
                    <TableCell className="text-sm whitespace-nowrap text-center">{r.booking_id || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-center">{r.date || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-center">{r.vehicle_reg_no || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-center">{r.customer_name || "—"}</TableCell>
                    <TableCell className="text-center w-12">
                      <button onClick={() => openRecord(r)} className="text-gray-500 hover:text-black" data-testid={`dq-open-${r.record_id}`}>
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
